import { useCallback, useState } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getAppExclusiveMessageEventProperties } from '@src/quince-components/src/atoms/greyBoxes/utils'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant/BaseProductVariant'
import { getFinalSaleEventProperties } from '@src/quince-site-common/src/utils/finalSaleEventUtility'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { getlbUserId } from '../../../utils/getlb-user-id'
import useDeviceInfo, { DEVICE_TYPES } from '../../useDeviceInfo'
import { buildAppDeepLinkUrl, buildPageUrl } from '../utils/appLink.utils'

export interface HandleShopInAppParams {
  product?: Product
  selectedOptions?: ProductVariantOption[]
  isButtonClick?: boolean
}

export function useShopInAppHandler() {
  const [isAppQRModalOpen, setIsAppQRModalOpen] = useState(false)

  const deviceInfo = useDeviceInfo()
  const { locale } = useLocaleContext()

  const handleShopInApp = useCallback(
    ({ product, selectedOptions, isButtonClick }: HandleShopInAppParams) => {
      const lbUserId = getlbUserId() ?? ''
      const pageLink = buildPageUrl({ locale, product, selectedOptions, lbUserId })
      const appQRLink = buildAppDeepLinkUrl(pageLink)

      const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
      const { is_app_exclusive, is_final_sale, promo_price } = getFinalSaleEventProperties(
        product ?? ({} as Product),
        selectedOptions ?? [],
      )
      const propertyOverrides = {
        discounted_price: promo_price ?? product?.variants?.[0]?.price,
        price: product?.variants?.[0]?.price,
      }
      const productProperties = {
        ...getAppExclusiveMessageEventProperties({
          product: product ?? ({} as Product),
          selectedOptions: selectedOptions ?? [],
          isFinalSaleProduct: !!is_final_sale,
          isColorAppExclusive: !!is_app_exclusive,
          propertyOverrides,
          action: 'shop_in_app',
        }),
      }
      if (isButtonClick) {
        trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
          ...productProperties,
        })
      }
      if (!isButtonClick) {
        trackEvent(TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_LINK_CLICKED, {
          context: {
            is_final_sale: is_final_sale ? 1 : 0,
            is_app_exclusive: is_app_exclusive ? 1 : 0,
          },
        })
      }
      if (isMobile) {
        window.location.href = appQRLink
      } else {
        setIsAppQRModalOpen(true)
        trackEvent(TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_MODAL_OPENED, {
          context: {
            is_final_sale: is_final_sale ? 1 : 0,
            is_app_exclusive: is_app_exclusive ? 1 : 0,
          },
        })
      }
    },
    [deviceInfo.deviceType, locale],
  )

  const handleCloseAppQRModal = useCallback(() => {
    setIsAppQRModalOpen(false)
  }, [])

  const handleOpenAppQRModal = useCallback(() => {
    setIsAppQRModalOpen(true)
  }, [])

  return { isAppQRModalOpen, handleShopInApp, handleCloseAppQRModal, handleOpenAppQRModal }
}
